/**
 * Server-Side State Management Hooks for TPRA Dashboard
 *
 * Custom hooks for managing pagination, search, sorting, and filtering
 * with server-side query parameter synchronization.
 */

import type { TPRAVendorsQueryParams } from '../types/vendors';
import type { RisksQueryParams } from '../types/risks';
import type { SessionsQueryParams } from '../types/sessions';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Hook for server-side pagination state management
 *
 * @param initialPage - Starting page number (default: 1)
 * @param initialPageSize - Starting page size (default: 50)
 * @returns Pagination state and handlers
 *
 * @example
 * ```typescript
 * const { page, pageSize, setPage, setPageSize, resetPage } = useServerPagination();
 * ```
 */
export function useServerPagination(initialPage = 1, initialPageSize = 50) {
  const [page, setPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1); // Reset to first page when page size changes
  }, []);

  const resetPage = useCallback(() => {
    setPage(1);
  }, []);

  return {
    page,
    pageSize,
    setPage: handlePageChange,
    setPageSize: handlePageSizeChange,
    resetPage,
  };
}

/**
 * Hook for server-side search with debouncing
 *
 * @param debounceMs - Debounce delay in milliseconds (default: 500)
 * @param onSearchChange - Optional callback when search changes (e.g., reset page)
 * @returns Search state and handlers
 *
 * @example
 * ```typescript
 * const { searchTerm, debouncedSearchTerm, setSearchTerm } = useServerSearch(500, resetPage);
 * // Use debouncedSearchTerm in API query params
 * ```
 */
export function useServerSearch(
  debounceMs = 500,
  onSearchChange?: () => void
) {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Clear existing timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Set new timeout
    debounceRef.current = setTimeout(() => {
      const searchChanged = searchTerm !== debouncedSearchTerm;
      setDebouncedSearchTerm(searchTerm);
      // Call callback when debounced search changes (e.g., reset page)
      if (searchChanged && onSearchChange) {
        onSearchChange();
      }
    }, debounceMs);

    // Cleanup on unmount or when searchTerm changes
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchTerm, debounceMs, debouncedSearchTerm, onSearchChange]);

  const handleSearchChange = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  return {
    searchTerm,
    debouncedSearchTerm,
    setSearchTerm: handleSearchChange,
  };
}

/**
 * Hook for server-side sorting state management
 *
 * @param initialSortBy - Initial sort field
 * @param initialSortOrder - Initial sort direction
 * @returns Sort state and handlers
 *
 * @example
 * ```typescript
 * const { sortBy, sortOrder, setSort } = useServerSort('vendor_name', 'asc');
 * ```
 */
export function useServerSort(
  initialSortBy?: TPRAVendorsQueryParams['sort_by'],
  initialSortOrder: 'asc' | 'desc' = 'asc'
) {
  const [sortBy, setSortBy] = useState<TPRAVendorsQueryParams['sort_by']>(initialSortBy);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(initialSortOrder);

  const handleSort = useCallback((column: TPRAVendorsQueryParams['sort_by']) => {
    if (sortBy === column) {
      // Toggle sort order if clicking same column
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      // Set new sort column with ascending order
      setSortBy(column);
      setSortOrder('asc');
    }
  }, [sortBy]);

  const resetSort = useCallback(() => {
    setSortBy(initialSortBy);
    setSortOrder(initialSortOrder);
  }, [initialSortBy, initialSortOrder]);

  return {
    sortBy,
    sortOrder,
    setSort: handleSort,
    resetSort,
  };
}

/**
 * Hook for server-side filter state management
 *
 * @param onFilterChange - Optional callback when filters change (e.g., reset page)
 * @returns Filter state and handlers
 *
 * @example
 * ```typescript
 * const { risk, status, setRisk, setStatus, clearFilters } = useServerFilters(resetPage);
 * ```
 */
export function useServerFilters(onFilterChange?: () => void) {
  const [risk, setRiskState] = useState<TPRAVendorsQueryParams['risk']>(undefined);
  const [status, setStatusState] = useState<TPRAVendorsQueryParams['status']>(undefined);

  const handleRiskChange = useCallback((newRisk: TPRAVendorsQueryParams['risk']) => {
    setRiskState(newRisk);
    onFilterChange?.();
  }, [onFilterChange]);

  const handleStatusChange = useCallback((newStatus: TPRAVendorsQueryParams['status']) => {
    setStatusState(newStatus);
    onFilterChange?.();
  }, [onFilterChange]);

  const clearFilters = useCallback(() => {
    setRiskState(undefined);
    setStatusState(undefined);
    onFilterChange?.();
  }, [onFilterChange]);

  return {
    risk,
    status,
    setRisk: handleRiskChange,
    setStatus: handleStatusChange,
    clearFilters,
  };
}

/**
 * Combined hook that manages all server-side state together
 *
 * Integrates pagination, search, sorting, and filtering into a single
 * query params object ready for API consumption.
 *
 * @param initialParams - Initial query parameters
 * @returns Combined state and query params for API
 *
 * @example
 * ```typescript
 * const {
 *   queryParams,
 *   pagination,
 *   search,
 *   sort,
 *   filters
 * } = useServerSideState({ page: 1, page_size: 50 });
 *
 * const { data } = useGetTPRAVendors(queryParams);
 * ```
 */
export function useServerSideState(
  initialParams: Partial<TPRAVendorsQueryParams> = {}
) {
  const pagination = useServerPagination(
    initialParams.page,
    initialParams.page_size
  );

  const search = useServerSearch(500, pagination.resetPage);

  const sort = useServerSort(
    initialParams.sort_by,
    initialParams.sort_order
  );

  const filters = useServerFilters(pagination.resetPage);

  // Combine all state into query params
  const queryParams: TPRAVendorsQueryParams = {
    page: pagination.page,
    page_size: pagination.pageSize,
    search: search.debouncedSearchTerm || undefined,
    sort_by: sort.sortBy,
    sort_order: sort.sortOrder,
    risk: filters.risk,
    status: filters.status,
  };

  return {
    queryParams,
    pagination,
    search,
    sort,
    filters,
  };
}

/**
 * Combined hook for Risks tab server-side state management
 * Specialized version for risks with severity filter instead of risk/status filters
 *
 * @param initialParams - Initial query parameters
 * @returns Combined state and query params for Risks API
 *
 * @example
 * ```typescript
 * const {
 *   queryParams,
 *   pagination,
 *   search,
 *   sort,
 *   severity
 * } = useRisksServerSideState({ page: 1, page_size: 25 });
 *
 * const { data } = useGetRisks(queryParams);
 * ```
 */
export function useRisksServerSideState(
  initialParams: Partial<RisksQueryParams> = {}
) {
  const pagination = useServerPagination(
    initialParams.page,
    initialParams.page_size ?? 25  // Risks default: 25
  );

  const search = useServerSearch(500, pagination.resetPage);

  // Use vendor sort hook but cast types for risks sort fields
  const sort = useServerSort(
    initialParams.sort_by as any ?? 'reporting_date',  // Default sort
    initialParams.sort_order ?? 'desc'                  // Default desc
  );

  // Severity filter state (replaces risk/status filters)
  const [severity, setSeverityState] = useState<RisksQueryParams['severity']>(
    initialParams.severity
  );

  const handleSeverityChange = useCallback(
    (newSeverity: RisksQueryParams['severity']) => {
      setSeverityState(newSeverity);
      pagination.resetPage();
    },
    [pagination]
  );

  // Combine all state into query params (cast sort fields to risks types)
  const queryParams: RisksQueryParams = {
    page: pagination.page,
    page_size: pagination.pageSize,
    search: search.debouncedSearchTerm || undefined,
    sort_by: sort.sortBy as RisksQueryParams['sort_by'],
    sort_order: sort.sortOrder,
    severity: severity,
  };

  return {
    queryParams,
    pagination,
    search,
    sort,
    severity: {
      value: severity,
      setValue: handleSeverityChange,
    },
  };
}

/**
 * Combined hook for Sessions tab server-side state management
 * Specialized version for sessions with search only (no sorting or filtering)
 *
 * @param initialParams - Initial query parameters
 * @returns Combined state and query params for Sessions API
 *
 * @example
 * ```typescript
 * const {
 *   queryParams,
 *   pagination,
 *   search
 * } = useSessionsServerSideState({ page: 1, page_size: 25 });
 *
 * const { data } = useGetSessions(queryParams, memberMapping);
 * ```
 */
export function useSessionsServerSideState(
  initialParams: Partial<SessionsQueryParams> = {}
) {
  // Pagination: 25 items per page (consistent with Risks)
  const pagination = useServerPagination(
    initialParams.page ?? 1,
    initialParams.page_size ?? 25
  );

  // Search: 500ms debounce, reset page on change
  const search = useServerSearch(500, pagination.resetPage);

  // Combine into query params
  const queryParams: SessionsQueryParams = {
    page: pagination.page,
    page_size: pagination.pageSize,
    search: search.debouncedSearchTerm || undefined,
  };

  return {
    queryParams,
    pagination,
    search,
  };
}
